import React, {ReactNode, useEffect, useState} from 'react'
import styled from '@emotion/styled'
import {anyPass, isEmpty, isNil} from 'ramda'
import {Size} from 'types/Theme'

// NOTE: This component uses an Icon size not supported by Icon component
import {User} from '@vio/atlas-assets'
import {getFirstAndLastInitials} from '@vio/core/src/auth/business/authentication'

import {cssTheme} from '../../../themes'
import minus from '../../../utils/minus'
import {Text, TextVariant} from '../Text'

type AvatarSize = Extract<Size, 'sm' | 'md' | 'lg' | 'xl'>

interface Props {
  /** Bottom icon */
  bottomIcon?: ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The size of the Avatar */
  size?: AvatarSize
  /** Appearance of avatar */
  type?: AvatarType
  /** User's picture */
  picture?: string
  /** User's name */
  name?: string
}

interface UserPhotoFallbackProps {
  size: AvatarSize
  name?: Props['name']
}

interface StandardAvatarProps {
  size: AvatarSize
  name?: Props['name']
}

interface AvatarStyles {
  borderColorValue?: string
  name?: Props['name']
  size: AvatarSize
}

export enum AvatarType {
  Picture = 'picture',
  Icon = 'icon'
}

export const avatarSizesMap = {
  sm: {
    size: 32,
    iconSize: 16,
    textVariant: 'titleXS'
  },
  md: {
    size: 56,
    iconSize: 32,
    textVariant: 'titleS'
  },
  lg: {
    size: 72,
    iconSize: 40,
    textVariant: 'titleM'
  },
  xl: {
    size: 96,
    iconSize: 48,
    textVariant: 'titleXL'
  }
}

const AvatarWrapper = styled.div<AvatarStyles>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${cssTheme.layout.radius.rounded};
  position: relative;
  width: 100%;
`

export const PictureAvatar = styled.img<StandardAvatarProps>`
  height: ${({size}) => avatarSizesMap[size].size}px;
  width: ${({size}) => avatarSizesMap[size].size}px;
  border-radius: ${cssTheme.layout.radius.rounded};
  object-fit: cover;
  display: block;
`

export const DefaultPictureWrapper = styled.div<StandardAvatarProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({size}) => avatarSizesMap[size].size}px;
  height: ${({size}) => avatarSizesMap[size].size}px;
  border-radius: ${cssTheme.layout.radius.rounded};
  background-color: ${cssTheme.colors.background.brand.inverse};
  color: ${cssTheme.colors.background.neutral.primary};
`

export const IconWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  bottom: ${minus(cssTheme.layout.spacing.s200)};
`

const StandardAvatar = ({size}: StandardAvatarProps) => (
  <DefaultPictureWrapper data-id="ProfilePicture" size={size}>
    <User size={avatarSizesMap[size].iconSize} />
  </DefaultPictureWrapper>
)

const UserPhotoFallback = ({name, size}: UserPhotoFallbackProps) => {
  const initials = getFirstAndLastInitials(name || '')

  return (
    <DefaultPictureWrapper data-id="ProfilePicture" size={size}>
      <Text variant={avatarSizesMap[size].textVariant as TextVariant}>
        {initials}
      </Text>
    </DefaultPictureWrapper>
  )
}

export const getShowAvatar = ({
  type,
  name,
  picture
}: {
  type: AvatarType
  name?: string
  picture?: string
}) =>
  type === AvatarType.Icon ||
  (anyPass([isNil, isEmpty])(name) && anyPass([isNil, isEmpty])(picture))

export const Avatar = ({
  picture,
  name,
  bottomIcon,
  size = 'sm',
  type = AvatarType.Icon,
  className
}: Props) => {
  const [shouldShowFallbackAvatar, setShouldShowFallbackAvatar] = useState(true)
  const shouldShowIconAvatar = getShowAvatar({type, name, picture})

  useEffect(() => {
    if (!picture) return setShouldShowFallbackAvatar(true)

    setShouldShowFallbackAvatar(false)
    const image = new Image()
    image.addEventListener('error', () => setShouldShowFallbackAvatar(true))
    image.src = picture
  }, [picture])

  const AvatarElement = shouldShowIconAvatar
    ? StandardAvatar
    : shouldShowFallbackAvatar
      ? UserPhotoFallback
      : PictureAvatar

  return (
    <AvatarWrapper size={size} className={className}>
      <AvatarElement
        data-id="ProfilePicture"
        className="fs-exclude"
        src={picture}
        size={size}
        alt={name}
        title={name}
        name={name}
      />
      {Boolean(bottomIcon) && <IconWrapper>{bottomIcon}</IconWrapper>}
    </AvatarWrapper>
  )
}
